<template>
  <PageLayout :title="$t('ai.assistant_title')" :subtitle="$t('ai.assistant_subtitle')">
    <template #actions>
      <router-link to="/ai/studio" class="btn btn-outline-primary mr-2">{{ $t('nav.design_studio') }}</router-link>
      <router-link to="/posts/approval" class="btn btn-outline-secondary mr-2">{{ $t('nav.approval') }}</router-link>
      <button class="btn btn-primary" @click="openCreate" :disabled="!availableAccounts.length">
        <i class="fas fa-plus mr-1"></i> {{ $t('ai.new_agent') }}
      </button>
    </template>

    <div class="row">
      <div class="col-lg-7 mb-30">
        <ContentCard>
          <h5 class="mb-3">{{ $t('ai.your_agents') }}</h5>
          <div v-if="loading && !agents.length" class="text-muted">{{ $t('common.loading') }}</div>
          <div v-else-if="!agents.length" class="text-center py-5 text-muted">
            <i class="fas fa-robot fa-2x mb-3 d-block"></i>
            <p class="mb-2">{{ $t('ai.no_agents') }}</p>
            <p class="small mb-3">{{ $t('ai.account_desc_hint') }}</p>
            <button class="btn btn-primary btn-sm" :disabled="!availableAccounts.length" @click="openCreate">
              {{ $t('ai.create_agent') }}
            </button>
            <p v-if="!accounts.length" class="small text-danger mt-2">{{ $t('ai.connect_ig_accounts') }}</p>
            <p v-else-if="!availableAccounts.length" class="small text-muted mt-2">{{ $t('ai.all_accounts_have_ai') }}</p>
          </div>
          <div v-for="agent in agents" :key="agent.id" class="agent-card">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <strong>{{ agent.name }}</strong>
                <span class="badge badge-light ml-2">@{{ agent.social_account?.username }}</span>
                <span class="badge ml-1" :class="agent.status === 'active' ? 'badge-success' : 'badge-secondary'">
                  {{ statusLabel(agent.status) }}
                </span>
                <p class="small text-muted mb-0 mt-2">{{ agent.account_brief }}</p>
              </div>
              <div class="text-nowrap">
                <button class="btn btn-xs btn-primary mr-1" @click="openRun(agent)">{{ $t('ai.generate') }}</button>
                <button class="btn btn-xs btn-outline-secondary mr-1" @click="openEdit(agent)">{{ $t('common.edit') }}</button>
                <button class="btn btn-xs btn-outline-danger" @click="onDelete(agent.id)">×</button>
              </div>
            </div>
          </div>
        </ContentCard>
      </div>

      <div class="col-lg-5 mb-30">
        <ContentCard>
          <h5 class="mb-3">{{ $t('ai.recent_generations') }}</h5>
          <div v-if="!runs.length" class="text-muted">{{ $t('ai.no_runs') }}</div>
          <div v-for="run in runs" :key="run.id" class="run-card">
            <div class="d-flex justify-content-between">
              <strong class="small">{{ run.topic }}</strong>
              <span class="badge" :class="runBadge(run.status)">{{ statusLabel(run.status) }}</span>
            </div>
            <div class="small text-muted mt-1">{{ truncate(run.caption, 90) }}</div>
            <div v-if="run.ideas?.length" class="small mt-1">
              {{ $t('ai.ideas_label') }}: {{ run.ideas.slice(0, 2).join(' · ') }}
            </div>
            <router-link v-if="run.post_id" :to="`/posts/${run.post_id}`" class="btn btn-xs btn-outline-secondary mt-2">
              {{ $t('ai.view_post') }}
            </router-link>
          </div>
        </ContentCard>
      </div>
    </div>

    <modal name="agent-modal" height="auto" :adaptive="true" :scrollable="true" width="560">
      <div class="modal-pad">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">{{ editingId ? $t('ai.edit_agent') : $t('ai.new_account_ai') }}</h5>
          <button type="button" class="btn btn-sm btn-link" @click="$modal.hide('agent-modal')">×</button>
        </div>
        <form @submit.prevent="onSave">
          <div class="mb-3" v-if="!editingId">
            <label class="small font-weight-bold">{{ $t('ai.ig_account') }}</label>
            <select v-model="form.social_account_id" class="form-control" required>
              <option disabled value="">{{ $t('composer.select_account') }}</option>
              <option v-for="acc in availableAccounts" :key="acc.id" :value="acc.id">@{{ acc.username }}</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('ai.agent_name') }}</label>
            <input v-model="form.name" class="form-control" :placeholder="$t('ai.agent_name_placeholder')" />
          </div>
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('ai.account_desc') }}</label>
            <textarea
              v-model="form.account_brief"
              class="form-control"
              rows="4"
              required
              :placeholder="$t('ai.account_brief_placeholder')"
            />
          </div>
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="small font-weight-bold">{{ $t('ai.tone') }}</label>
              <select v-model="form.tone" class="form-control">
                <option value="professional">{{ $t('ai.tone_professional') }}</option>
                <option value="casual">{{ $t('ai.tone_casual') }}</option>
                <option value="playful">{{ $t('ai.tone_playful') }}</option>
                <option value="inspirational">{{ $t('ai.tone_inspirational') }}</option>
                <option value="luxury">{{ $t('ai.tone_luxury') }}</option>
              </select>
            </div>
            <div class="col-md-6 mb-3">
              <label class="small font-weight-bold">{{ $t('ai.language') }}</label>
              <select v-model="form.language" class="form-control">
                <option value="id">{{ $t('locale.indonesian') }}</option>
                <option value="en">{{ $t('locale.english') }}</option>
                <option value="id+en">{{ $t('ai.bilingual') }}</option>
              </select>
            </div>
          </div>
          <div class="mb-3" v-if="editingId">
            <label class="small font-weight-bold">{{ $t('global.status') }}</label>
            <select v-model="form.status" class="form-control">
              <option value="active">{{ $t('status.active') }}</option>
              <option value="paused">{{ $t('status.paused') }}</option>
            </select>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary mr-2" @click="$modal.hide('agent-modal')">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? $t('common.saving') : $t('common.save') }}</button>
          </div>
        </form>
      </div>
    </modal>

    <modal name="run-modal" height="auto" :adaptive="true" width="480">
      <div class="modal-pad">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">{{ $t('ai.generate_post') }}</h5>
          <button type="button" class="btn btn-sm btn-link" @click="$modal.hide('run-modal')">×</button>
        </div>
        <p class="small text-muted" v-if="runAgent">
          {{ $t('ai.run_for', { username: runAgent.social_account?.username }) }}
        </p>
        <form @submit.prevent="onRun">
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('ai.topic_brief') }}</label>
            <textarea v-model="runForm.topic" class="form-control" rows="3" required :placeholder="$t('ai.topic_run_placeholder')" />
          </div>
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('ai.post_type') }}</label>
            <select v-model="runForm.post_type" class="form-control">
              <option value="feed">{{ $t('composer.feed') }}</option>
              <option value="story">{{ $t('composer.story') }}</option>
              <option value="reel">{{ $t('composer.reel') }}</option>
            </select>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary mr-2" @click="$modal.hide('run-modal')">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="running">
              {{ running ? $t('ai.generating') : $t('ai.generate_approval') }}
            </button>
          </div>
        </form>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

export default {
  mixins: [i18nPage],
  data() {
    return {
      saving: false,
      running: false,
      editingId: null,
      runAgent: null,
      form: {
        social_account_id: '', name: '', account_brief: '',
        tone: 'professional', language: 'id', status: 'active',
      },
      runForm: { topic: '', post_type: 'feed' },
    }
  },
  computed: {
    ...mapGetters('ai', ['agents', 'runs', 'loading']),
    ...mapGetters('app', ['accounts']),
    usedAccountIds() {
      return new Set(this.agents.map((a) => a.social_account_id))
    },
    availableAccounts() {
      if (this.editingId) return this.accounts
      return this.accounts.filter((a) => !this.usedAccountIds.has(a.id))
    },
  },
  async created() {
    await Promise.all([this.fetchAgents(), this.fetchRuns(), this.fetchAccounts()])
  },
  methods: {
    ...mapActions('ai', ['fetchAgents', 'createAgent', 'updateAgent', 'deleteAgent', 'runAgent', 'fetchRuns']),
    ...mapActions('app', ['fetchAccounts']),
    openCreate() {
      this.editingId = null
      this.form = {
        social_account_id: this.availableAccounts[0]?.id || '',
        name: '', account_brief: '', tone: 'professional', language: 'id', status: 'active',
      }
      this.$modal.show('agent-modal')
    },
    openEdit(agent) {
      this.editingId = agent.id
      this.form = {
        social_account_id: agent.social_account_id,
        name: agent.name,
        account_brief: agent.account_brief,
        tone: agent.tone || 'professional',
        language: agent.language || 'id',
        status: agent.status || 'active',
      }
      this.$modal.show('agent-modal')
    },
    openRun(agent) {
      this.runAgent = agent
      this.runForm = { topic: '', post_type: 'feed' }
      this.$modal.show('run-modal')
    },
    async onSave() {
      this.saving = true
      try {
        if (this.editingId) {
          await this.updateAgent({
            id: this.editingId,
            payload: {
              name: this.form.name,
              account_brief: this.form.account_brief,
              tone: this.form.tone,
              language: this.form.language,
              status: this.form.status,
            },
          })
        } else {
          await this.createAgent({
            social_account_id: Number(this.form.social_account_id),
            name: this.form.name,
            account_brief: this.form.account_brief,
            tone: this.form.tone,
            language: this.form.language,
          })
        }
        this.$modal.hide('agent-modal')
      } catch (e) {
        await this.$appAlert(e.response?.data?.message || this.$t('ai.save_failed'))
      } finally {
        this.saving = false
      }
    },
    async onDelete(id) {
      const ok = await this.$confirm(this.$t('ai.delete_agent_confirm'), {
        confirmText: this.$t('common.delete'),
        danger: true,
      })
      if (!ok) return
      await this.deleteAgent(id)
    },
    async onRun() {
      if (!this.runAgent) return
      this.running = true
      try {
        await this.runAgent({
          id: this.runAgent.id,
          payload: { ...this.runForm },
        })
        this.$modal.hide('run-modal')
        this.$router.push('/posts/approval')
      } catch (e) {
        await this.$appAlert(e.response?.data?.message || this.$t('ai.generate_failed'))
      } finally {
        this.running = false
      }
    },
    runBadge(s) {
      return ({ pending_approval: 'badge-warning', failed: 'badge-danger', running: 'badge-info' })[s] || 'badge-secondary'
    },
    truncate(t, n) {
      if (!t) return '-'
      return t.length > n ? `${t.slice(0, n)}…` : t
    },
  },
}
</script>

<style scoped>
.agent-card, .run-card {
  border: 1px solid #eef1f4;
  border-radius: 10px;
  padding: 0.9rem;
  margin-bottom: 0.65rem;
  background: #fff;
}
.modal-pad { padding: 1.25rem 1.35rem 1.5rem; }
</style>
